<template>
    <QDialog
        v-model="dialog"
        noBackdropDismiss
        transitionShow="slide-up"
        transitionHide="slide-down"
        @hide="handleClose"
    >
        <QCard class="TracePreviewDialog">
            <QBar class="bg-primary text-white">
                <div>{{ $t('visualization.traces.preview') }}: {{ traceKey }}</div>
                <QSpace />
                <QBtn
                    dense
                    flat
                    color="white"
                    icon="close"
                    @click="handleClose"
                />
            </QBar>

            <QCardSection class="relative-position">
                <q-resize-observer @resize="handleResize" />

                <div v-if="!isLoading && events.length === 0" class="flex justify-center items-center q-pa-md">
                    <WText :size="16">
                        {{ $t('visualization.traces.plot.noEvents') }}
                    </WText>
                </div>

                <v-chart
                    v-else
                    ref="chart"
                    class="Chart"
                    :option="chartOption"
                    autoresize
                />

                <q-inner-loading :showing="isLoading" />
            </QCardSection>
        </QCard>
    </QDialog>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import {
    Api, apiRequest, notifyError,
} from '@/api'
import { filtersStorageMixin } from '@/mixins'
import { getColorArray } from '@/services/colormap'

const MAX_PAGES = 100

export default {
    name: 'TracePreviewDialog',
    mixins: [filtersStorageMixin],
    props: {
        show: VueTypes.bool.def(false),
        processId: VueTypes.oneOfType([VueTypes.string, VueTypes.number]).isRequired,
        traceKey: VueTypes.oneOfType([VueTypes.string, VueTypes.number]),
    },
    emits: ['close'],
    data() {
        return {
            dialog: false,
            isLoading: false,
            events: [],
            chartOption: {
                grid: {
                    left: 160,
                    right: 20,
                    top: 10,
                    bottom: 30,
                },
                tooltip: {
                    trigger: 'item',
                    confine: true,
                    formatter: (params) => {
                        const activity = params?.data?.activity
                        const value = params?.value || params?.data?.value
                        const startMs = value?.[0]
                        const endMs = value?.[1]
                        const startStr = Number.isFinite(startMs) ? moment.utc(startMs).format('YYYY-MM-DD HH:mm:ss') : '-'
                        const endStr = Number.isFinite(endMs) ? moment.utc(endMs).format('YYYY-MM-DD HH:mm:ss') : '-'
                        return `<div><strong>${activity || '-'}</strong></div><div>${startStr} → ${endStr}</div>`
                    },
                },
                xAxis: {
                    type: 'time',
                    axisLine: { show: true },
                    splitLine: { show: true },
                },
                yAxis: {
                    type: 'category',
                    inverse: true,
                    data: [],
                    axisTick: { show: false },
                    axisLine: { show: false },
                    axisLabel: {
                        formatter: (value) => value,
                    },
                },
                series: [],
            },
        }
    },
    watch: {
        show: {
            handler(value) {
                this.dialog = !!value
                if (value) {
                    this.load()
                }
            },
            immediate: true,
        },
        traceKey(newVal, oldVal) {
            if (!this.dialog) return
            if (`${newVal || ''}` === `${oldVal || ''}`) return
            this.load()
        },
    },
    methods: {
        handleClose() {
            this.dialog = false
            this.$emit('close')
        },
        handleResize() {
            if (!this.$refs.chart) return
            setTimeout(() => {
                this.$refs.chart?.resize?.()
            }, 0)
        },
        formatParams({
            page,
            limit,
            search,
        }) {
            return this.buildVisualizationParams({
                ...(limit ? { start: page } : {}),
                ...(limit ? { limit } : {}),
                attributes: [],
                search: search || '',
                sortBy: 'START_TIME',
            })
        },
        normalizeRow(row) {
            const {
                traceKey,
                start,
                end,
                activity,
            } = row || {}
            const startNorm = start
            const endNorm = end

            const startMs = typeof startNorm === 'number' ? startNorm : moment.utc(startNorm).valueOf()
            const endMsRaw = typeof endNorm === 'number' ? endNorm : moment.utc(endNorm).valueOf()

            const safeStartMs = Number.isFinite(startMs) ? startMs : null
            const safeEndMs = Number.isFinite(endMsRaw) ? endMsRaw : null
            const endMs = safeStartMs !== null
                ? Math.max(safeEndMs !== null ? safeEndMs : safeStartMs, safeStartMs + 1)
                : null

            return {
                traceKey,
                start: startNorm,
                end: endNorm,
                startMs: safeStartMs,
                endMs,
                activity,
            }
        },
        async fetchAllEventsForTraceKey(traceKey) {
            const limit = 5000
            const search = `${traceKey}`

            const first = await apiRequest(Api().visualizations.traces({
                processId: this.processId,
                params: this.formatParams({
                    page: 0,
                    limit,
                    search,
                }),
            }))

            const total = Number(first?.total || 0)
            const pages = Math.min(MAX_PAGES, Math.ceil(total / limit) || 1)

            const all = [...(first?.data || [])]

            for (let page = 1; page < pages; page += 1) {
                // eslint-disable-next-line no-await-in-loop
                const res = await apiRequest(Api().visualizations.traces({
                    processId: this.processId,
                    params: this.formatParams({
                        page,
                        limit,
                        search,
                    }),
                }))
                const chunk = res?.data || []
                if (!chunk.length) break
                all.push(...chunk)
                if (all.length >= total) break
            }

            return all.map(this.normalizeRow)
        },
        buildChartOption(events) {
            const valid = (events || [])
                .filter((e) => Number.isFinite(e.startMs) && Number.isFinite(e.endMs))
                .slice()
                .sort((a, b) => (a.startMs - b.startMs) || (a.endMs - b.endMs))

            // Y-axis lanes: one per activity, ordered by first appearance.
            const activityOrder = []
            const activityIndex = new Map()
            valid.forEach((e) => {
                const name = e.activity || '-'
                if (activityIndex.has(name)) return
                activityIndex.set(name, activityOrder.length)
                activityOrder.push(name)
            })

            const colors = getColorArray(activityOrder.length)
            const colorByActivity = new Map(activityOrder.map((a, idx) => [a, colors[idx]]))

            const data = valid.map((e) => {
                const name = e.activity || '-'
                const yIndex = activityIndex.get(name)
                const color = colorByActivity.get(name) || '#0994ba'
                return {
                    // Use numeric y index for category axis to avoid coord() issues with long/duplicated labels.
                    value: [e.startMs, e.endMs, yIndex],
                    activity: name,
                    start: e.start,
                    end: e.end,
                    itemStyle: {
                        color,
                        borderColor: color,
                        borderWidth: 1,
                    },
                }
            })

            const series = [
                {
                    type: 'custom',
                    coordinateSystem: 'cartesian2d',
                    renderItem: (params, api) => {
                        const yIndex = api.value(2)
                        const startCoord = api.coord([api.value(0), yIndex])
                        const endCoord = api.coord([api.value(1), yIndex])
                        const height = api.size([0, 1])[1] * 0.7

                        const originalDataItem = data[params.dataIndex] || {}
                        const itemStyle = originalDataItem.itemStyle || params?.data?.itemStyle || {}
                        const fill = itemStyle.color || '#0994ba'
                        const stroke = itemStyle.borderColor || fill
                        const lineWidth = itemStyle.borderWidth || 0

                        const rectShape = {
                            x: startCoord[0],
                            y: startCoord[1] - height / 2,
                            width: Math.max(endCoord[0] - startCoord[0], 1),
                            height,
                        }

                        return {
                            type: 'rect',
                            shape: rectShape,
                            style: {
                                fill,
                                stroke,
                                lineWidth,
                            },
                        }
                    },
                    encode: {
                        x: [0, 1],
                        y: 2,
                    },
                    data,
                },
            ]

            this.chartOption = {
                ...this.chartOption,
                yAxis: {
                    ...this.chartOption.yAxis,
                    data: activityOrder,
                },
                series,
            }

            this.$nextTick(() => this.handleResize())
        },
        async load() {
            if (!this.traceKey) {
                this.events = []
                this.buildChartOption([])
                return
            }

            this.isLoading = true
            try {
                const events = await this.fetchAllEventsForTraceKey(this.traceKey)
                this.events = events
                this.buildChartOption(events)
            } catch (e) {
                this.events = []
                notifyError(e)
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<style scoped lang="scss">
.TracePreviewDialog {
    width: 980px;
    max-width: 1024px;
}

.Chart {
    width: 100%;
    height: 520px;
}
</style>
